"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.GameEngine = void 0;
const types_1 = require("../types");
const Player_1 = require("../models/Player");
const ConditionEvaluator_1 = require("./ConditionEvaluator");
const EffectProcessor_1 = require("./EffectProcessor");
const VariableStore_1 = require("./VariableStore");
const VariablesConfigParser_1 = require("./VariablesConfigParser");
const InterpolationPipeline_1 = require("./InterpolationPipeline");
const ConditionEvaluationError_1 = require("../utils/ConditionEvaluationError");
const pathConfig_1 = require("../utils/pathConfig");
const ids_1 = require("../types/ids");
const NpcManager_1 = require("./NpcManager");
const runNpcUpdate_1 = require("./runNpcUpdate");
const npcTickConfig_1 = require("./npcTickConfig");
const EffectTracker_1 = require("./EffectTracker");
const entities_1 = require("../entities");
const ScriptEvaluator_1 = require("./ScriptEvaluator");
const interpolatedInlineOptions_1 = require("./interpolatedInlineOptions");
const RuntimeSnapshot_1 = require("./RuntimeSnapshot");
const openCharacterRuntime_1 = require("../utils/openCharacterRuntime");
const characterInstanceNames_1 = require("../utils/characterInstanceNames");
const fs = __importStar(require("fs"));
const DEFAULT_USER_INPUT_MAX_LENGTH = 200;
const ABSOLUTE_USER_INPUT_MAX_LENGTH = 4000;
const SNAPSHOT_ANALYTICS_ADVENTURE_ID = 'storynity-headquarters';
const EMPTY_NPC_TICK_METRICS = {
    plannedTotal: 0,
    ran: 0,
    truncatedByLimit: 0,
    skippedByBudget: 0,
    scriptFailures: 0,
    avgUpdateMs: 0,
    wallTimeMs: 0,
    variablesChanged: false
};
class GameEngine {
    constructor(gameSession, adventureManager, cacheService, friendsService, adventureInviteService, asyncOperationService, inviteLinkService, emailService, emailActionTokenService, verificationCodeService, analyticsService, sharedVariablesService, guestAdventureService, entitiesService) {
        this.npcNowMsProvider = () => Date.now();
        this.npcTickObservability = {};
        this.npcTickSafety = { ...npcTickConfig_1.DEFAULT_NPC_TICK_SAFETY_LIMITS };
        this.lastNpcTickMetrics = { ...EMPTY_NPC_TICK_METRICS };
        /** Deferred navigation after shared-variable writes are flushed by the host. */
        this.pendingOptionContinuations = new Map();
        this.gameSession = gameSession;
        this.adventureManager = adventureManager;
        this.variableStore = new VariableStore_1.VariableStore(gameSession.id, cacheService);
        this.interpolationPipeline = new InterpolationPipeline_1.InterpolationPipeline(this.variableStore, this.adventureManager, friendsService, adventureInviteService, asyncOperationService, inviteLinkService, analyticsService, entitiesService);
        this.friendsService = friendsService;
        this.adventureInviteService = adventureInviteService;
        this.asyncOperationService = asyncOperationService;
        this.inviteLinkService = inviteLinkService;
        this.emailService = emailService;
        this.emailActionTokenService = emailActionTokenService;
        this.verificationCodeService = verificationCodeService;
        this.analyticsService = analyticsService;
        this.sharedVariablesService = sharedVariablesService;
        this.guestAdventureService = guestAdventureService;
        this.entitiesService = entitiesService;
        this.npcManager = new NpcManager_1.NpcManager(this.variableStore, () => this.adventureManager.getAdventure(this.gameSession.adventureId), () => {
            const p = this.gameSession.players[0];
            return {
                playerId: p?.id ?? ids_1.IdHelpers.toPlayerId('__npc_anchor__'),
                userId: p?.userId ?? 'anonymous',
                currentSceneId: p?.currentScene ?? ''
            };
        }, (npcId, previous, next) => {
            if (this.npcTickObservability.debugEnabled) {
                this.emitNpcTick('debug', 'NPC location set (manager)', { npcId, previous, next });
            }
        });
        // Load variables configuration
        this.loadVariablesConfig();
    }
    /** NPC location / active set management for this session. */
    getNpcManager() {
        return this.npcManager;
    }
    /** Access the session variable store (e.g. for test harness assertions). */
    getVariableStore() {
        return this.variableStore;
    }
    /** Await in-flight variable persistence (e.g. before JOIN_ADVENTURE session switches). */
    async flushPendingVariablePersistence() {
        await this.variableStore.flushPendingVariablePersistence();
    }
    /**
     * Await in-flight entity persistence (current-entity pointers, creates, variable writes)
     * before session switches or engine eviction / rehydrate.
     */
    async flushPendingEntitiesPersistence() {
        if (this.entitiesService) {
            await this.entitiesService.flushPendingPersistence();
        }
    }
    /** Optional shared-variables write queue (backend flushes during selectOption). */
    getSharedVariablesService() {
        return this.sharedVariablesService;
    }
    /** Optional guest-adventure create queue (backend flushes during selectOption). */
    getGuestAdventureService() {
        return this.guestAdventureService;
    }
    /** Session entity store (create/destroy/visit). */
    getEntitiesService() {
        return this.entitiesService;
    }
    /** Foreign library lookup for script helpers (warmed by host before scene present). */
    getForeignEntityLookup() {
        return this.foreignEntityLookup;
    }
    setForeignEntityLookup(lookup) {
        this.foreignEntityLookup = lookup;
        this.interpolationPipeline.setForeignEntityLookup(lookup);
    }
    /**
     * Present/refresh recovery: if the player is on an entity-dependent scene without a valid
     * current entity, move them to {@link MISSING_ENTITY_SCENE_ID}.
     * @returns true when the player was redirected.
     */
    ensureMissingEntityRedirect(playerId) {
        const player = this.getPlayer(playerId);
        if (!player) {
            return false;
        }
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        const entitiesService = this.entitiesService;
        if (!adventure || !entitiesService) {
            return false;
        }
        if (!(0, entities_1.playerNeedsMissingEntityRedirect)(adventure, player.currentScene, entitiesService.getCurrentEntityId(player.id), uuid => !!entitiesService.getEntity(uuid))) {
            return false;
        }
        const recovery = adventure.getScene(entities_1.MISSING_ENTITY_SCENE_ID);
        if (!recovery) {
            console.warn(`[GameEngine] Player ${playerId} needs missing_entity redirect but scene '${entities_1.MISSING_ENTITY_SCENE_ID}' is not in the adventure`);
            return false;
        }
        player.moveToScene(entities_1.MISSING_ENTITY_SCENE_ID);
        this.gameSession.updatePlayer(player);
        return true;
    }
    /** Apply {@link ensureMissingEntityRedirect} for every player in the session. */
    ensureMissingEntityRedirectForAllPlayers() {
        const redirected = [];
        for (const p of this.gameSession.players) {
            if (this.ensureMissingEntityRedirect(p.id)) {
                redirected.push(p.id);
            }
        }
        return redirected;
    }
    runEffectScript(effectScript, player) {
        if (!effectScript) {
            return (0, EffectTracker_1.emptyEffectResult)();
        }
        return EffectProcessor_1.EffectProcessor.process(effectScript, player, this.gameSession, this.variableStore, this.adventureManager, this.friendsService, this.adventureInviteService, this.asyncOperationService, this.inviteLinkService, this.emailService, this.emailActionTokenService, this.verificationCodeService, this.analyticsService, this.sharedVariablesService, this.guestAdventureService, this.entitiesService, this.foreignEntityLookup);
    }
    /** Run the inline `update` script for one NPC (errors are logged, not thrown). */
    runNpcUpdateForNpc(npcId, onUpdateFailure) {
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        return (0, runNpcUpdate_1.runNpcUpdate)(npcId, adventure, {
            gameSession: this.gameSession,
            variableStore: this.variableStore,
            adventureManager: this.adventureManager,
            friendsService: this.friendsService,
            adventureInviteService: this.adventureInviteService,
            asyncOperationService: this.asyncOperationService,
            inviteLinkService: this.inviteLinkService,
            getNowMs: this.npcNowMsProvider,
            onUpdateFailure
        });
    }
    /** Optional debug logging and custom log sink for NPC ticks. */
    setNpcTickObservability(options) {
        this.npcTickObservability = { ...options };
    }
    /** Optional caps: max NPCs per tick, wall-time budget per tick. */
    setNpcTickSafetyLimits(limits) {
        this.npcTickSafety = { ...this.npcTickSafety, ...limits };
    }
    /** Metrics from the last {@link runNpcTickForActiveNpcs} call. */
    getNpcTickMetricsSnapshot() {
        return this.lastNpcTickMetrics;
    }
    emitNpcTick(level, message, meta) {
        if (level === 'debug' && !this.npcTickObservability.debugEnabled) {
            return;
        }
        const log = this.npcTickObservability.log ?? npcTickConfig_1.defaultNpcTickLog;
        try {
            log(level, message, meta);
        }
        catch {
            /* logging must never throw */
        }
    }
    nowMsForNpcTickBudget() {
        if (typeof globalThis.performance !== 'undefined' && typeof globalThis.performance.now === 'function') {
            return globalThis.performance.now();
        }
        return Date.now();
    }
    /** Allows tests/hosts to provide deterministic time for both state-change and time-based NPC ticks. */
    setNpcNowProvider(getNowMs) {
        this.npcNowMsProvider = getNowMs;
    }
    /**
     * Runs `update` for every NPC that currently has a non-empty `character_location`, in sorted id order.
     * Used after a successful {@link selectOption} (state-change tick) and by {@link NpcSessionTickScheduler}.
     */
    runNpcTickForActiveNpcs() {
        const tickEffects = (0, EffectTracker_1.emptyEffectResult)();
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        if (!adventure?.getNpcDefinitions().length) {
            this.lastNpcTickMetrics = { ...EMPTY_NPC_TICK_METRICS };
            return { variablesChanged: false, itemChanges: tickEffects.itemChanges, toasts: tickEffects.toasts };
        }
        let activeIds = this.npcManager.getActiveNpcIds();
        const plannedTotal = activeIds.length;
        let truncatedByLimit = 0;
        const maxNpcs = this.npcTickSafety.maxNpcsPerTick;
        if (maxNpcs !== undefined && maxNpcs > 0 && activeIds.length > maxNpcs) {
            truncatedByLimit = activeIds.length - maxNpcs;
            activeIds = activeIds.slice(0, maxNpcs);
            this.emitNpcTick('warn', 'NPC tick: maxNpcsPerTick exceeded; truncating', {
                maxNpcsPerTick: maxNpcs,
                truncatedByLimit,
                plannedTotal
            });
        }
        this.variableStore.beginNpcTickBatch();
        const budgetMs = this.npcTickSafety.maxWallTimeMsPerTick;
        const tickStart = this.nowMsForNpcTickBudget();
        let skippedByBudget = 0;
        let scriptFailures = 0;
        const durations = [];
        const debug = this.npcTickObservability.debugEnabled === true;
        for (let i = 0; i < activeIds.length; i++) {
            const npcId = activeIds[i];
            if (budgetMs !== undefined && budgetMs > 0) {
                if (this.nowMsForNpcTickBudget() - tickStart > budgetMs) {
                    skippedByBudget = activeIds.length - i;
                    this.emitNpcTick('warn', 'NPC tick: time budget exceeded; skipping remaining', {
                        maxWallTimeMsPerTick: budgetMs,
                        skippedByBudget,
                        atNpcId: npcId
                    });
                    break;
                }
            }
            const locBefore = debug ? this.npcManager.getWorldLocationForNpc(npcId) : '';
            const runStart = this.nowMsForNpcTickBudget();
            const npcEffectResult = this.runNpcUpdateForNpc(npcId, () => {
                scriptFailures++;
            });
            (0, EffectTracker_1.mergeEffectInto)(tickEffects, npcEffectResult);
            durations.push(this.nowMsForNpcTickBudget() - runStart);
            if (debug) {
                const locAfter = this.npcManager.getWorldLocationForNpc(npcId);
                if (locBefore !== locAfter) {
                    this.emitNpcTick('debug', 'NPC location changed (update)', {
                        npcId,
                        previous: locBefore,
                        next: locAfter
                    });
                }
            }
        }
        const variablesChanged = this.variableStore.endNpcTickBatch();
        const wallTimeMs = this.nowMsForNpcTickBudget() - tickStart;
        const ran = durations.length;
        const avgUpdateMs = ran > 0 ? durations.reduce((a, b) => a + b, 0) / ran : 0;
        this.lastNpcTickMetrics = {
            plannedTotal,
            ran,
            truncatedByLimit,
            skippedByBudget,
            scriptFailures,
            avgUpdateMs,
            wallTimeMs,
            variablesChanged
        };
        if (debug) {
            this.emitNpcTick('debug', 'NPC tick summary', { ...this.lastNpcTickMetrics });
        }
        return {
            variablesChanged,
            itemChanges: tickEffects.itemChanges,
            toasts: tickEffects.toasts
        };
    }
    /** Merge NPC tick item/toast side effects into accumulators (call after option/auto effects). */
    appendNpcTickEffects(itemChanges, toasts) {
        const tick = this.runNpcTickForActiveNpcs();
        itemChanges.push(...tick.itemChanges);
        toasts.push(...tick.toasts);
    }
    getGameSession() {
        return this.gameSession;
    }
    /**
     * Evaluate an activity {@code Requires:} script for the given player (e.g. {@code <Activity-List>} row hydration).
     * Empty or whitespace-only scripts are treated as satisfied.
     */
    evaluateConditionScriptForPlayer(script, playerId) {
        const trimmed = (script ?? '').trim();
        if (!trimmed) {
            return true;
        }
        const player = this.getPlayer(playerId);
        if (!player) {
            return false;
        }
        try {
            return ScriptEvaluator_1.ScriptEvaluator.executeCondition(trimmed, player, this.gameSession, this.variableStore, this.adventureManager, this.friendsService, this.adventureInviteService, this.asyncOperationService, this.inviteLinkService, this.emailService, this.emailActionTokenService, this.verificationCodeService, this.analyticsService, this.entitiesService);
        }
        catch (error) {
            console.error('[GameEngine] evaluateConditionScriptForPlayer failed:', error);
            return false;
        }
    }
    /**
     * Set the cache service for backend integration
     */
    setCacheService(cacheService) {
        this.variableStore.setCacheService(cacheService);
    }
    getPlayer(playerId) {
        return this.gameSession.getPlayer(playerId);
    }
    /**
     * Add a player to the game session
     */
    addPlayer(player) {
        this.gameSession.addPlayer(player);
    }
    getCurrentScene(playerId) {
        const player = this.getPlayer(playerId);
        if (!player)
            return undefined;
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        if (!adventure)
            return undefined;
        return adventure.getScene(player.currentScene);
    }
    /**
     * Get interpolated scene data for display purposes
     */
    getInterpolatedScene(playerId) {
        const player = this.getPlayer(playerId);
        if (!player) {
            console.warn(`[GameEngine] getInterpolatedScene: Player ${playerId} not found`);
            return undefined;
        }
        // Debug logging for friend request conditionals
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        if (!adventure) {
            return undefined;
        }
        const scene = adventure.getScene(player.currentScene);
        if (!scene) {
            return undefined;
        }
        // Check if scene has friend request conditionals
        const hasFriendRequestConditional = scene.sections?.some((s) => s.description?.elements?.some((e) => e.condition?.includes('getPendingReceivedRequestCount')));
        // Interpolate variables in scene descriptions and options
        return this.interpolateScene(scene, player);
    }
    /**
     * Interpolate Shared-Data block children after shared aggregates are resolved.
     */
    interpolateSharedDataBlockContent(playerId, elements, injectedVariables) {
        const player = this.getPlayer(playerId);
        if (!player) {
            return elements;
        }
        return this.interpolationPipeline.interpolateSharedDataElements(elements, {
            playerId: player.id,
            characterName: player.characterName,
            currentSceneId: player.currentScene,
            userId: player.userId || 'default',
            player,
            gameSession: this.gameSession,
            injectedVariables
        });
    }
    getAvailableOptions(playerId) {
        const player = this.getPlayer(playerId);
        const scene = this.getCurrentScene(playerId);
        if (!player || !scene)
            return [];
        // Completed players should not have any options available
        if (player.isCompleted()) {
            return [];
        }
        return scene.getAllOptions().filter((option) => ConditionEvaluator_1.ConditionEvaluator.evaluate(option.condition, player, this.gameSession, this.adventureManager, this.variableStore, undefined, this.friendsService, this.adventureInviteService, this.asyncOperationService, this.inviteLinkService, this.emailService, this.emailActionTokenService, this.verificationCodeService, this.analyticsService, this.entitiesService));
    }
    getBackOptionId(playerId) {
        const scene = this.getCurrentScene(playerId);
        if (!scene)
            return undefined;
        return scene.getBackOptionId();
    }
    getAvailableOptionsBySection(playerId) {
        const player = this.getPlayer(playerId);
        const scene = this.getCurrentScene(playerId);
        if (!player || !scene)
            return [];
        // Completed players should not have any options available
        if (player.isCompleted()) {
            return [];
        }
        return scene.sections.map((section, sectionIndex) => {
            const combinedOptions = scene.getSectionOptions(sectionIndex);
            const filteredOptions = combinedOptions.filter((option) => {
                const isFinishOption = option.target === 'FINISH';
                if (isFinishOption && player.isCompleted()) {
                    return false;
                }
                try {
                    return ConditionEvaluator_1.ConditionEvaluator.evaluate(option.condition, player, this.gameSession, this.adventureManager, this.variableStore, option.id, this.friendsService, this.adventureInviteService, this.asyncOperationService, this.inviteLinkService, this.emailService, this.emailActionTokenService, this.verificationCodeService, this.analyticsService, this.entitiesService, this.foreignEntityLookup);
                }
                catch (error) {
                    if (error instanceof ConditionEvaluationError_1.ConditionEvaluationError) {
                        console.error(`❌ Condition evaluation error for option '${error.optionId}':`, error.message);
                        console.error(`   Condition: ${error.conditionScript}`);
                        console.error(`   Player: ${error.playerId}`);
                        console.error(`   Cause:`, error.cause);
                        return false;
                    }
                    console.error(`❌ Unexpected error in condition evaluation for option '${option.id}':`, error);
                    return false;
                }
            });
            return {
                sectionIndex,
                options: filteredOptions
            };
        }).filter((sectionData) => sectionData.options.length > 0);
    }
    /**
     * Get available options by section with interpolated text
     */
    getInterpolatedAvailableOptionsBySection(playerId) {
        const player = this.getPlayer(playerId);
        const interpolatedScene = this.getInterpolatedScene(playerId);
        if (!player || !interpolatedScene)
            return [];
        // Completed players should not have any options available
        if (player.isCompleted()) {
            return [];
        }
        return interpolatedScene.sections.map((section, sectionIndex) => {
            const originalScene = this.getCurrentScene(playerId);
            if (!originalScene) {
                return {
                    sectionIndex,
                    options: []
                };
            }
            const originalOptions = originalScene.getSectionOptions(sectionIndex);
            const filteredOptions = originalOptions.filter((option) => {
                const isFinishOption = option.target === 'FINISH';
                if (isFinishOption && player.isCompleted()) {
                    return false;
                }
                try {
                    return ConditionEvaluator_1.ConditionEvaluator.evaluate(option.condition, player, this.gameSession, this.adventureManager, this.variableStore, option.id, this.friendsService, this.adventureInviteService, this.asyncOperationService, this.inviteLinkService, this.emailService, this.emailActionTokenService, this.verificationCodeService, this.analyticsService, this.entitiesService, this.foreignEntityLookup);
                }
                catch (error) {
                    if (error instanceof ConditionEvaluationError_1.ConditionEvaluationError) {
                        console.error(`❌ Condition evaluation error for option '${error.optionId}':`, error.message);
                        console.error(`   Condition: ${error.conditionScript}`);
                        console.error(`   Player: ${error.playerId}`);
                        console.error(`   Cause:`, error.cause);
                        return false;
                    }
                    console.error(`❌ Unexpected error in condition evaluation for option '${option.id}':`, error);
                    return false;
                }
            });
            const enrichedOptions = filteredOptions.map((option) => {
                const interpolatedOption = (section.options || []).find((opt) => opt.id === option.id);
                return interpolatedOption ?? option;
            });
            return {
                sectionIndex,
                options: enrichedOptions
            };
        }).filter((sectionData) => sectionData.options.length > 0);
    }
    hasFinishOption(playerId) {
        const scene = this.getCurrentScene(playerId);
        return scene ? scene.hasFinishOption() : false;
    }
    hasLeaveOption(playerId) {
        const scene = this.getCurrentScene(playerId);
        return scene ? scene.hasLeaveOption() : false;
    }
    selectFinishOption(playerId) {
        const player = this.getPlayer(playerId);
        const scene = this.getCurrentScene(playerId);
        if (!player || !scene || !scene.hasFinishOption()) {
            return { success: false, message: 'FinishOption not available in current scene' };
        }
        if (player.isCompleted()) {
            return { success: false, message: 'Player has already completed the adventure' };
        }
        // Mark player as completed
        player.setCompleted(true);
        this.gameSession.updatePlayer(player);
        // Check if all players are completed and complete the game if so
        this.completeGame();
        const finishOptions = scene.getFinishOptions();
        let message = 'Adventure Complete';
        if (finishOptions.length > 0) {
            if (typeof finishOptions[0].text === 'string') {
                message = finishOptions[0].text;
            }
            else {
                // Handle rich text - extract plain text from first element
                const richText = finishOptions[0].text;
                if (richText.elements && richText.elements.length > 0) {
                    const firstElement = richText.elements[0];
                    if (firstElement.content && firstElement.content.length > 0) {
                        const firstContent = firstElement.content[0];
                        if (typeof firstContent === 'string') {
                            message = firstContent;
                        }
                        else if (firstContent.content && typeof firstContent.content === 'string') {
                            message = firstContent.content;
                        }
                    }
                }
            }
        }
        return { success: true, message };
    }
    isPlayerCompleted(playerId) {
        const player = this.getPlayer(playerId);
        return player ? player.isCompleted() : false;
    }
    getWaitingOptions(playerId) {
        const player = this.getPlayer(playerId);
        const scene = this.getCurrentScene(playerId);
        if (!player || !scene)
            return [];
        return scene.getAllOptions().filter((option) => {
            // Only return options with a Wait Message defined
            if (!option.waitMessage)
                return false;
            // If option has no conditions, it's not a waiting option
            if (!option.condition)
                return false;
            return !ConditionEvaluator_1.ConditionEvaluator.evaluate(option.condition, player, this.gameSession, this.adventureManager, this.variableStore, undefined, this.friendsService, this.adventureInviteService, this.asyncOperationService, this.inviteLinkService, this.emailService, this.emailActionTokenService, this.verificationCodeService, this.analyticsService, this.entitiesService, this.foreignEntityLookup);
        });
    }
    /**
     * Get waiting options with interpolated wait messages
     */
    getInterpolatedWaitingOptions(playerId) {
        const player = this.getPlayer(playerId);
        const interpolatedScene = this.getInterpolatedScene(playerId);
        if (!player || !interpolatedScene)
            return [];
        // Get all options from the interpolated scene (plain object structure)
        const allOptions = interpolatedScene.sections ?
            interpolatedScene.sections.flatMap((section) => section.options || []) : [];
        return allOptions.filter((option) => {
            // Only return options with a Wait Message defined
            if (!option.waitMessage)
                return false;
            // If option has no conditions, it's not a waiting option
            if (!option.condition)
                return false;
            // Apply condition evaluation (using original scene for conditions)
            const originalScene = this.getCurrentScene(playerId);
            if (!originalScene)
                return false;
            const originalOption = originalScene.findOptionById(option.id);
            if (!originalOption)
                return false;
            return !ConditionEvaluator_1.ConditionEvaluator.evaluate(originalOption.condition, player, this.gameSession, this.adventureManager, this.variableStore, undefined, this.friendsService, this.adventureInviteService, undefined, this.inviteLinkService, this.emailService, this.emailActionTokenService, this.verificationCodeService, this.analyticsService, this.entitiesService, this.foreignEntityLookup);
        });
    }
    /**
     * Returns waiting options whose autoTransition flag is set AND whose condition is now TRUE.
     * Called by the backend after player state changes to trigger automatic scene transitions.
     */
    getReadyAutoTransitionOptions(playerId) {
        const player = this.getPlayer(playerId);
        const scene = this.getCurrentScene(playerId);
        if (!player || !scene)
            return [];
        return scene.getAllOptions().filter((option) => {
            if (!option.waitMessage || !option.condition || !option.autoTransition)
                return false;
            return ConditionEvaluator_1.ConditionEvaluator.evaluate(option.condition, player, this.gameSession, this.adventureManager, this.variableStore, undefined, this.friendsService, this.adventureInviteService, this.asyncOperationService, this.inviteLinkService, this.emailService, this.emailActionTokenService, this.verificationCodeService, this.analyticsService, this.entitiesService, this.foreignEntityLookup);
        });
    }
    /**
     * Whether the backend should call `AnalyticsScriptService.refreshSnapshot()` before processing
     * an option click for this player (static adventure scan + selected option/target heuristics).
     */
    shouldRefreshAnalyticsSnapshotForPendingOptionSelect(playerId, optionId) {
        if (this.gameSession.adventureId !== SNAPSHOT_ANALYTICS_ADVENTURE_ID) {
            return false;
        }
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        if (!adventure) {
            return true;
        }
        const player = this.getPlayer(playerId);
        const scene = this.getCurrentScene(playerId);
        if (!player || !scene) {
            return true;
        }
        let selectedOption = scene.findOptionById(optionId);
        if (!selectedOption) {
            const interpolatedScene = this.interpolateScene(scene, player);
            selectedOption = (0, interpolatedInlineOptions_1.findInterpolatedInlineOptionById)(interpolatedScene, optionId);
        }
        if (!selectedOption) {
            return true;
        }
        const rawTarget = typeof selectedOption.target === 'string' ? selectedOption.target : '';
        const interpolatedNonScriptTarget = rawTarget && !this.isScriptTarget(rawTarget) ? this.interpolateTarget(player, rawTarget) : undefined;
        return adventure.shouldRefreshAnalyticsSnapshotForPendingOptionSelection({
            currentSceneId: player.currentScene,
            selectedOption,
            rawTarget,
            interpolatedNonScriptTarget,
        });
    }
    /**
     * Whether the backend should call `AnalyticsScriptService.refreshSnapshot()` before interpolating
     * the player's current scene (e.g. `getCurrentScene`).
     */
    shouldRefreshAnalyticsSnapshotForCurrentSceneDisplay(playerId) {
        if (this.gameSession.adventureId !== SNAPSHOT_ANALYTICS_ADVENTURE_ID) {
            return false;
        }
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        const player = this.getPlayer(playerId);
        if (!adventure || !player) {
            return true;
        }
        return adventure.shouldRefreshAnalyticsSnapshotForSceneDisplay(player.currentScene);
    }
    /**
     * Whether the backend should call `AnalyticsClient.getDisplayName()` before interpolating
     * the player's current scene (static adventure scan of display-name variables and alias helpers).
     */
    shouldSyncAnalyticsDisplayNameForCurrentSceneDisplay(playerId) {
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        const player = this.getPlayer(playerId);
        if (!adventure || !player) {
            return false;
        }
        return adventure.shouldSyncAnalyticsDisplayNameForSceneDisplay(player.currentScene);
    }
    /**
     * Whether the backend should call `AnalyticsClient.getDisplayName()` before processing
     * an option click for this player (static adventure scan + selected option/target heuristics).
     */
    shouldSyncAnalyticsDisplayNameForPendingOptionSelect(playerId, optionId) {
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        if (!adventure || !adventure.usesAnalyticsDisplayName()) {
            return false;
        }
        const player = this.getPlayer(playerId);
        const scene = this.getCurrentScene(playerId);
        if (!player || !scene) {
            return false;
        }
        let selectedOption = scene.findOptionById(optionId);
        if (!selectedOption) {
            const interpolatedScene = this.interpolateScene(scene, player);
            selectedOption = (0, interpolatedInlineOptions_1.findInterpolatedInlineOptionById)(interpolatedScene, optionId);
        }
        if (!selectedOption) {
            return false;
        }
        const rawTarget = typeof selectedOption.target === 'string' ? selectedOption.target : '';
        const interpolatedNonScriptTarget = rawTarget && !this.isScriptTarget(rawTarget) ? this.interpolateTarget(player, rawTarget) : undefined;
        return adventure.shouldSyncAnalyticsDisplayNameForPendingOptionSelection({
            currentSceneId: player.currentScene,
            selectedOption,
            rawTarget,
            interpolatedNonScriptTarget,
        });
    }
    selectOption(playerId, optionId) {
        const player = this.getPlayer(playerId);
        const scene = this.getCurrentScene(playerId);
        if (!player || !scene)
            return { success: false, itemChanges: [], toasts: [] };
        let selectedOption = scene.findOptionById(optionId);
        if (!selectedOption) {
            const interpolatedScene = this.interpolateScene(scene, player);
            selectedOption = (0, interpolatedInlineOptions_1.findInterpolatedInlineOptionById)(interpolatedScene, optionId);
        }
        if (!selectedOption)
            return { success: false, itemChanges: [], toasts: [] };
        // Check if the option is available (conditions are met)
        if (!ConditionEvaluator_1.ConditionEvaluator.evaluate(selectedOption.condition, player, this.gameSession, this.adventureManager, this.variableStore, undefined, this.friendsService, this.adventureInviteService, undefined, this.inviteLinkService, this.emailService, this.emailActionTokenService, this.verificationCodeService, undefined, this.entitiesService, this.foreignEntityLookup)) {
            return { success: false, itemChanges: [], toasts: [] };
        }
        const allItemChanges = [];
        const allToasts = [];
        const mergeEffectResult = (effectResult) => {
            allItemChanges.push(...effectResult.itemChanges);
            allToasts.push(...effectResult.toasts);
        };
        const rawTargetForNavigation = typeof selectedOption.target === 'string' ? selectedOption.target : '';
        this.pendingMenuReturnContext = this.captureMenuReturnContext(player, rawTargetForNavigation);
        // Process option effects and track item changes
        if (selectedOption.effect) {
            const effectResult = this.runEffectScript(selectedOption.effect, player);
            mergeEffectResult(effectResult);
        }
        // Get the target (may be script target that needs evaluation)
        let target = selectedOption.target;
        // Check if target is a script target and evaluate it if needed
        if (target && this.isScriptTarget(target)) {
            try {
                const evaluatedTarget = this.evaluateTargetScript(target, player, this.gameSession);
                // If evaluation returns empty string, log warning and fail
                if (!evaluatedTarget || evaluatedTarget.trim().length === 0) {
                    console.warn(`Script target evaluation returned empty string: ${target}`);
                    return { success: false, itemChanges: allItemChanges, toasts: allToasts };
                }
                // Use evaluated target for subsequent processing
                target = evaluatedTarget;
            }
            catch (error) {
                // Script evaluation failed - log error and return failure
                console.error(`❌ Script target evaluation failed for option '${optionId}':`, error);
                return { success: false, itemChanges: allItemChanges, toasts: allToasts };
            }
        }
        if (this.hasPendingSharedWrites(playerId) || this.hasPendingGuestAdventureOperations(playerId)) {
            this.pendingOptionContinuations.set(playerId, {
                optionId,
                target,
                rawTarget: selectedOption.target,
                itemChanges: [...allItemChanges],
                toasts: [...allToasts]
            });
            return {
                success: true,
                itemChanges: allItemChanges,
                toasts: allToasts,
                pendingSharedWriteFlush: this.hasPendingSharedWrites(playerId),
                pendingGuestAdventureFlush: this.hasPendingGuestAdventureOperations(playerId)
            };
        }
        return this.finishOptionSelection(playerId, optionId, target, selectedOption.target, allItemChanges, allToasts);
    }
    /** Resume option navigation after the host flushes pending shared-variable writes. */
    completePendingOptionSelection(playerId) {
        const pending = this.pendingOptionContinuations.get(playerId);
        if (!pending) {
            return { success: false, itemChanges: [], toasts: [] };
        }
        this.pendingOptionContinuations.delete(playerId);
        let target = pending.target;
        if (this.isScriptTarget(pending.rawTarget)) {
            const player = this.getPlayer(playerId);
            if (player) {
                try {
                    const reevaluatedTarget = this.evaluateTargetScript(pending.rawTarget, player, this.gameSession);
                    if (reevaluatedTarget && reevaluatedTarget.trim().length > 0) {
                        target = reevaluatedTarget;
                    }
                }
                catch (error) {
                    console.error('Failed to re-evaluate script target after pending flush:', error);
                }
            }
        }
        return this.finishOptionSelection(playerId, pending.optionId, target, pending.rawTarget, pending.itemChanges, pending.toasts);
    }
    /** Drop a deferred option when shared-variable flush fails (effects already applied). */
    clearPendingOptionContinuation(playerId) {
        this.pendingOptionContinuations.delete(playerId);
    }
    hasPendingSharedWrites(playerId) {
        const service = this.sharedVariablesService;
        if (!service) {
            return false;
        }
        return service.getPendingWrites(playerId).length > 0;
    }
    hasPendingGuestAdventureCreates(playerId) {
        const service = this.guestAdventureService;
        if (!service) {
            return false;
        }
        return service.getPendingCreates(playerId).length > 0;
    }
    hasPendingGuestAdventureDeletes(playerId) {
        const service = this.guestAdventureService;
        if (!service) {
            return false;
        }
        return service.getPendingDeletes(playerId).length > 0;
    }
    hasPendingGuestAdventureOperations(playerId) {
        return (this.hasPendingGuestAdventureCreates(playerId) ||
            this.hasPendingGuestAdventureDeletes(playerId));
    }
    /**
     * Re-run an option's effect script (e.g. after guest-adventure flush so createGuestAdventure returns the link).
     */
    replayOptionEffects(playerId, optionId) {
        const player = this.getPlayer(playerId);
        const scene = this.getCurrentScene(playerId);
        if (!player || !scene) {
            return (0, EffectTracker_1.emptyEffectResult)();
        }
        let selectedOption = scene.findOptionById(optionId);
        if (!selectedOption) {
            const interpolatedScene = this.interpolateScene(scene, player);
            selectedOption = (0, interpolatedInlineOptions_1.findInterpolatedInlineOptionById)(interpolatedScene, optionId);
        }
        if (!selectedOption?.effect) {
            return (0, EffectTracker_1.emptyEffectResult)();
        }
        return this.runEffectScript(selectedOption.effect, player);
    }
    finishOptionSelection(playerId, optionId, target, rawTarget, allItemChanges, allToasts) {
        const player = this.getPlayer(playerId);
        if (!player) {
            return { success: false, itemChanges: allItemChanges, toasts: allToasts };
        }
        const mergeEffectResult = (effectResult) => {
            allItemChanges.push(...effectResult.itemChanges);
            allToasts.push(...effectResult.toasts);
        };
        // Handle finish options (complete adventure; player stays in session)
        if (target === 'FINISH') {
            player.setCompleted(true);
            this.gameSession.updatePlayer(player);
            this.completeGame();
            this.appendNpcTickEffects(allItemChanges, allToasts);
            return { success: true, itemChanges: allItemChanges, toasts: allToasts };
        }
        // Handle leave option (leave adventure; backend removes player and frees slot)
        if (target === 'LEAVE') {
            this.appendNpcTickEffects(allItemChanges, allToasts);
            return { success: true, itemChanges: allItemChanges, toasts: allToasts, action: 'LEAVE' };
        }
        // Handle special targets: MAIN_MENU and QUIT_ADVENTURE
        if (target === 'MAIN_MENU') {
            // Return action indicator for home navigation (effects already processed)
            this.appendNpcTickEffects(allItemChanges, allToasts);
            return { success: true, itemChanges: allItemChanges, toasts: allToasts, action: 'MAIN_MENU' };
        }
        if (target === 'QUIT_ADVENTURE') {
            // Return action indicator for quit adventure (effects already processed)
            this.appendNpcTickEffects(allItemChanges, allToasts);
            return { success: true, itemChanges: allItemChanges, toasts: allToasts, action: 'QUIT_ADVENTURE' };
        }
        // Handle START_ADVENTURE target: START_ADVENTURE:adventureId
        if (target.startsWith('START_ADVENTURE:')) {
            const adventureId = target.substring('START_ADVENTURE:'.length).trim();
            // Validate adventure ID exists
            if (!adventureId) {
                console.error('START_ADVENTURE target missing adventure ID');
                return { success: false, itemChanges: allItemChanges, toasts: allToasts };
            }
            // Validate adventure exists in AdventureManager
            const targetAdventure = this.adventureManager.getAdventure(adventureId);
            if (!targetAdventure) {
                console.error(`START_ADVENTURE target references non-existent adventure: ${adventureId}`);
                return { success: false, itemChanges: allItemChanges, toasts: allToasts };
            }
            // Return action indicator with adventure ID (effects already processed)
            this.appendNpcTickEffects(allItemChanges, allToasts);
            return { success: true, itemChanges: allItemChanges, toasts: allToasts, action: 'START_ADVENTURE', adventureId };
        }
        // Handle START_OR_RESUME_ADVENTURE target: START_OR_RESUME_ADVENTURE:adventureId
        // Same as START_ADVENTURE but backend will resume existing session if user has one
        if (target.startsWith('START_OR_RESUME_ADVENTURE:')) {
            const adventureId = target.substring('START_OR_RESUME_ADVENTURE:'.length).trim();
            if (!adventureId) {
                console.error('START_OR_RESUME_ADVENTURE target missing adventure ID');
                return { success: false, itemChanges: allItemChanges, toasts: allToasts };
            }
            const targetAdventure = this.adventureManager.getAdventure(adventureId);
            if (!targetAdventure) {
                console.error(`START_OR_RESUME_ADVENTURE target references non-existent adventure: ${adventureId}`);
                return { success: false, itemChanges: allItemChanges, toasts: allToasts };
            }
            this.appendNpcTickEffects(allItemChanges, allToasts);
            return { success: true, itemChanges: allItemChanges, toasts: allToasts, action: 'START_OR_RESUME_ADVENTURE', adventureId };
        }
        // Handle RETURN_TO_ADVENTURE: pop opening_session_id + join target (backend performs pop/join)
        if (target === 'RETURN_TO_ADVENTURE') {
            const topOpening = this.peekStack(player.id, 'opening_session_id');
            if (!topOpening) {
                return { success: false, itemChanges: allItemChanges, toasts: allToasts };
            }
            return { success: true, itemChanges: allItemChanges, toasts: allToasts, action: 'RETURN_TO_ADVENTURE' };
        }
        // Handle JOIN_ADVENTURE target: JOIN_ADVENTURE:sessionId:playerSlot
        if (target.startsWith('JOIN_ADVENTURE:')) {
            const targetParts = target.substring('JOIN_ADVENTURE:'.length).trim().split(':');
            if (targetParts.length !== 2) {
                console.error('JOIN_ADVENTURE target must have format: JOIN_ADVENTURE:sessionId:playerSlot');
                return { success: false, itemChanges: allItemChanges, toasts: allToasts };
            }
            const sessionId = targetParts[0].trim();
            const playerSlot = targetParts[1].trim();
            // Validate sessionId and playerSlot exist
            if (!sessionId || !playerSlot) {
                console.error('JOIN_ADVENTURE target missing sessionId or playerSlot');
                return { success: false, itemChanges: allItemChanges, toasts: allToasts };
            }
            // Return action indicator with sessionId and playerSlot (effects already processed)
            // Note: The backend will handle the actual session join operation when processing this action
            this.appendNpcTickEffects(allItemChanges, allToasts);
            return { success: true, itemChanges: allItemChanges, toasts: allToasts, action: 'JOIN_ADVENTURE', sessionId, playerSlot };
        }
        // Handle LOGGED_IN target: Navigate to authenticated user's system adventure
        if (target === 'LOGGED_IN') {
            // Return action indicator for logged-in navigation (effects already processed)
            // The backend will handle getting/creating the user's system adventure session
            this.appendNpcTickEffects(allItemChanges, allToasts);
            return { success: true, itemChanges: allItemChanges, toasts: allToasts, action: 'LOGGED_IN' };
        }
        // Handle SIGN_OUT target: Sign out user and return as guest
        if (target === 'SIGN_OUT') {
            // Return action indicator for sign out (effects already processed)
            // The frontend will handle signing out and creating guest session
            this.appendNpcTickEffects(allItemChanges, allToasts);
            return { success: true, itemChanges: allItemChanges, toasts: allToasts, action: 'SIGN_OUT' };
        }
        // Handle DELETE_ACCOUNT target: Delete user account and return as guest
        if (target === 'DELETE_ACCOUNT') {
            // Return action indicator for account deletion (effects already processed)
            // The frontend will handle account deletion and creating guest session
            this.appendNpcTickEffects(allItemChanges, allToasts);
            return { success: true, itemChanges: allItemChanges, toasts: allToasts, action: 'DELETE_ACCOUNT' };
        }
        let interpolatedTarget = this.isReturnSceneTarget(rawTarget)
            ? this.resolveReturnSceneTarget(player, rawTarget)
            : this.interpolateTarget(player, target);
        // Validate that the target scene exists (for regular scene targets)
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        if (adventure) {
            const targetScene = adventure.getScene(interpolatedTarget);
            if (!targetScene) {
                console.error(`❌ Target scene not found: ${interpolatedTarget} (evaluated from: ${rawTarget})`);
                return { success: false, itemChanges: allItemChanges, toasts: allToasts };
            }
        }
        // If the target was ${return_scene}, pop the stack since we're navigating back
        if (this.isReturnSceneTarget(rawTarget)) {
            this.applyReturnSceneStackPop(player.id, interpolatedTarget);
        }
        // Move player to target scene
        player.moveToScene(interpolatedTarget);
        // Update character location if the new scene defines one
        if (adventure) {
            const targetScene = adventure.getScene(interpolatedTarget);
            if (targetScene?.locationTag) {
                this.setVariable(playerId, 'character_location', targetScene.locationTag);
            }
        }
        // Set scene enter timestamp for getSceneOpenTime()
        this.setVariable(playerId, 'user_scene_enter_timestamp', Math.floor(Date.now() / 1000));
        // Process auto effects of the new scene and track item changes
        if (adventure) {
            const newScene = adventure.getScene(player.currentScene);
            if (newScene) {
                if (newScene.autoEffect) {
                    const autoEffectResult = this.runEffectScript(newScene.autoEffect, player);
                    mergeEffectResult(autoEffectResult);
                }
            }
            else {
                console.log(`❌ Scene not found: ${interpolatedTarget}`);
            }
        }
        // Entity-dependent present recovery (e.g. Back onto a destroyed visit scene).
        // Runs after auto-effects; does not interrupt author Target: after same-option destroy
        // when that Target is a non-dependent hub.
        this.ensureMissingEntityRedirect(playerId);
        // Update player in game session
        this.gameSession.updatePlayer(player);
        this.appendNpcTickEffects(allItemChanges, allToasts);
        return { success: true, itemChanges: allItemChanges, toasts: allToasts };
    }
    processAutoEffects(playerId) {
        const player = this.getPlayer(playerId);
        const scene = this.getCurrentScene(playerId);
        if (!player || !scene)
            return (0, EffectTracker_1.emptyEffectResult)();
        // Set scene enter timestamp if not set (initial scene when game starts)
        const context = {
            playerId: player.id,
            characterName: player.characterName,
            currentSceneId: player.currentScene,
            userId: player.userId || 'anonymous'
        };
        const existing = this.variableStore.getVariable('user_scene_enter_timestamp', context);
        if (existing === undefined || existing === null) {
            this.setVariable(playerId, 'user_scene_enter_timestamp', Math.floor(Date.now() / 1000));
        }
        if (scene.autoEffect) {
            const effectResult = this.runEffectScript(scene.autoEffect, player);
            this.gameSession.updatePlayer(player);
            return effectResult;
        }
        return (0, EffectTracker_1.emptyEffectResult)();
    }
    isPlayerAtEnd(playerId) {
        const player = this.getPlayer(playerId);
        const scene = this.getCurrentScene(playerId);
        if (!scene || !player)
            return true;
        // Player is at end if they have completed the adventure
        if (player.isCompleted())
            return true;
        // Player is at end if they have a FinishOption available
        if (scene.hasFinishOption())
            return true;
        // Player is at end if they have no available options (legacy behavior)
        return scene.getAllOptions().length === 0;
    }
    getGameState(playerId) {
        return {
            session: this.gameSession,
            currentPlayer: playerId
        };
    }
    async startGame() {
        this.gameSession.setStatus(types_1.GameStatus.ACTIVE);
        // Initialize location inventories from adventure definition
        this.initializeLocationInventories();
        // Initialize scene variables for ALL scenes (shared across session)
        await this.initializeSceneVariables();
        // Initialize variables for all existing players (asynchronously)
        await this.initializeAllPlayerVariables();
        // Process auto effects for all players' starting scenes
        for (const player of this.gameSession.players) {
            this.processAutoEffects(player.id);
        }
    }
    initializeLocationInventories() {
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        if (!adventure || !adventure.locations)
            return;
        // Initialize each location's inventory from the adventure definition
        for (const location of adventure.locations) {
            this.gameSession.initializeLocationInventory(location.tag, location.startingInventory);
        }
    }
    pauseGame() {
        this.gameSession.setStatus(types_1.GameStatus.PAUSED);
    }
    resumeGame() {
        this.gameSession.setStatus(types_1.GameStatus.ACTIVE);
    }
    checkGameCompletion() {
        return this.gameSession.players.every(player => this.isPlayerCompleted(player.id));
    }
    completeGame() {
        if (this.checkGameCompletion()) {
            this.gameSession.setStatus(types_1.GameStatus.COMPLETED);
        }
    }
    getSessionStatus() {
        return this.gameSession.status;
    }
    /**
     * Get a shallow copy of all location inventories keyed by location tag
     */
    getAllLocationInventories() {
        // Return a new Map with cloned arrays to avoid external mutation
        const copy = new Map();
        for (const [locationTag, items] of this.gameSession.locationInventories.entries()) {
            copy.set(locationTag, [...items]);
        }
        return copy;
    }
    /**
     * Get a copy of a single location inventory by tag
     */
    getLocationInventory(locationTag) {
        return this.gameSession.getLocationInventory(locationTag);
    }
    /**
     * Create a player from a player definition or open-group slot (`player2`, …).
     */
    async createPlayerFromDefinition(playerId, playerName) {
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        if (!adventure) {
            return { success: false, message: 'Adventure not found' };
        }
        if (this.gameSession.getPlayerByPlayerName(playerName)) {
            return { success: false, message: `Player '${playerName}' already exists` };
        }
        const playerDef = (0, openCharacterRuntime_1.findPlayerDefinitionForSlot)(adventure.players, playerName);
        if (!playerDef) {
            return { success: false, message: `Player definition '${playerName}' not found` };
        }
        if (playerDef.kind === 'open') {
            const remaining = (0, openCharacterRuntime_1.getOpenGroupRemaining)(this.gameSession, adventure.players);
            if (remaining <= 0) {
                return { success: false, message: `Open player group '${playerDef.playerName}' is at Max capacity` };
            }
        }
        const identity = (0, openCharacterRuntime_1.resolveStartingCharacterIdentity)(this.gameSession, adventure.characters, playerDef);
        if ('error' in identity) {
            return { success: false, message: identity.error };
        }
        const { characterName, template: character } = identity;
        // Create the player with instance-or-fixed character identity
        const player = this.gameSession.createPlayerFromDefinition(playerId, playerName, characterName, character.startingScene);
        // Initialize variables for the new player (asynchronously)
        await this.initializePlayerVariables(player);
        // Store last userId for this character (character-scoped)
        if (player.userId) {
            this.setVariable(player.id, 'character_last_user_id', player.userId, {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId
            });
        }
        // Initialize character location if the starting scene defines one
        const startingScene = adventure.getScene(character.startingScene);
        if (startingScene?.locationTag) {
            this.setVariable(player.id, 'character_location', startingScene.locationTag);
        }
        // Process auto effects for the starting scene
        this.processAutoEffects(player.id);
        return { success: true, player };
    }
    /**
     * Get the next available player slot name for incremental player creation
     * (fixed empty seats first, then next open-group `playerN` when capacity remains).
     */
    getNextAvailablePlayerName() {
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        if (!adventure || !adventure.players) {
            return null;
        }
        for (const playerDef of (0, openCharacterRuntime_1.getFixedPlayerDefinitions)(adventure.players)) {
            if (!this.gameSession.getPlayerByPlayerName(playerDef.playerName)) {
                return playerDef.playerName;
            }
        }
        return (0, openCharacterRuntime_1.allocateNextOpenSlotName)(this.gameSession, adventure.players);
    }
    /**
     * Bind the player to a character template or instance (open templates allocate/bind instances).
     */
    setPlayerCurrentCharacter(playerId, requestedName) {
        const player = this.getPlayer(playerId);
        if (!player) {
            return { success: false, message: 'Player not found' };
        }
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        if (!adventure) {
            return { success: false, message: 'Adventure not found' };
        }
        const resolved = (0, openCharacterRuntime_1.resolveSetCurrentCharacterName)(this.gameSession, adventure.characters, player, requestedName);
        if ('error' in resolved) {
            return { success: false, message: resolved.error };
        }
        player.setCurrentCharacter(resolved.instanceOrFixedName);
        this.gameSession.updatePlayer(player);
        this.seedCharacterInstanceVariables(resolved.instanceOrFixedName);
        return { success: true, characterName: resolved.instanceOrFixedName };
    }
    /**
     * Ensure character-scoped defaults exist for an open instance name (or fixed template).
     * Safe to call repeatedly; does not overwrite existing values.
     */
    seedCharacterInstanceVariables(characterName) {
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        if (!adventure) {
            return;
        }
        const template = adventure.getCharacter(characterName);
        if (!template) {
            return;
        }
        const charVars = new Map();
        if (adventure.characterVariables) {
            for (const variable of adventure.characterVariables) {
                charVars.set(variable.name, {
                    name: variable.name,
                    value: variable.defaultValue,
                    type: variable.type
                });
            }
        }
        if (template.variables) {
            for (const variable of template.variables) {
                charVars.set(variable.name, {
                    name: variable.name,
                    value: variable.defaultValue,
                    type: variable.type
                });
            }
        }
        this.variableStore.ensureCharacterVariableDefaults(template.name, charVars);
        if ((0, characterInstanceNames_1.isCharacterInstanceName)(characterName)) {
            this.variableStore.ensureCharacterVariableDefaults(characterName, charVars);
        }
    }
    /**
     * Load variables configuration from VariablesConfig.md
     */
    loadVariablesConfig() {
        try {
            // Load using PathConfig
            const configPath = pathConfig_1.PathConfig.getVariablesConfigPath();
            if (fs.existsSync(configPath)) {
                const content = fs.readFileSync(configPath, 'utf-8');
                this.variablesConfig = VariablesConfigParser_1.VariablesConfigParser.parse(content);
            }
            else {
                console.warn('⚠️ VariablesConfig.md not found, using empty configuration');
                this.variablesConfig = {
                    userVariables: [],
                    mandatoryVariables: {
                        player: [],
                        character: []
                    }
                };
            }
        }
        catch (error) {
            console.error('Failed to load VariablesConfig.md:', error);
            // Use empty configuration as fallback
            this.variablesConfig = {
                userVariables: [],
                mandatoryVariables: {
                    player: [],
                    character: []
                }
            };
        }
    }
    /**
     * Interpolate variables in a scene
     */
    interpolateScene(scene, player) {
        const context = {
            playerId: player.id,
            characterName: player.characterName,
            currentSceneId: player.currentScene,
            userId: player.userId || 'default',
            player: player,
            gameSession: this.gameSession
        };
        // Create a deep copy of the scene to avoid mutating the original
        const interpolatedScene = JSON.parse(JSON.stringify(scene));
        const hasSectionOptionsGetter = typeof scene.getSectionOptions === 'function';
        if (hasSectionOptionsGetter && interpolatedScene.sections) {
            for (let i = 0; i < interpolatedScene.sections.length; i++) {
                const originalSection = scene.sections?.[i];
                const explicitOptions = originalSection?.options ?? [];
                interpolatedScene.sections[i].options = explicitOptions.map((option) => ({ ...option }));
            }
        }
        // Interpolate sections
        if (interpolatedScene.sections) {
            for (let i = 0; i < interpolatedScene.sections.length; i++) {
                const section = interpolatedScene.sections[i];
                // Interpolate description
                if (section.description) {
                    section.description = this.interpolationPipeline.interpolateRichText(section.description, context);
                    (0, interpolatedInlineOptions_1.assignMissingInterpolatedInlineOptionIds)(section.description, interpolatedScene.id, i);
                }
                // Interpolate options
                if (section.options) {
                    for (let j = 0; j < section.options.length; j++) {
                        const option = section.options[j];
                        // Interpolate option text
                        if (option.text) {
                            option.text = this.interpolationPipeline.interpolateRichText(option.text, context);
                        }
                        // Interpolate wait message
                        if (option.waitMessage) {
                            option.waitMessage = this.interpolationPipeline.interpolateRichText(option.waitMessage, context);
                        }
                        // Interpolate target (for dynamic scene targets)
                        if (option.target) {
                            option.target = this.interpolationPipeline.interpolateString(option.target, context);
                        }
                    }
                }
            }
        }
        return interpolatedScene;
    }
    /**
     * Resolve a character portrait for the given player, including variable interpolation.
     */
    getInterpolatedCharacterPortrait(playerId, characterName) {
        const player = this.getPlayer(playerId);
        if (!player) {
            console.warn(`getInterpolatedCharacterPortrait: Player '${playerId}' not found`);
            return 'generic';
        }
        const targetCharacterName = characterName ?? player.characterName;
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        if (!adventure) {
            console.warn(`getInterpolatedCharacterPortrait: Adventure '${this.gameSession.adventureId}' not found`);
            return 'generic';
        }
        const character = adventure.getCharacter(targetCharacterName);
        if (!character) {
            console.warn(`getInterpolatedCharacterPortrait: Character '${targetCharacterName}' not found`);
            return 'generic';
        }
        const basePortrait = (character.portraitId ?? 'generic').trim();
        if (!basePortrait.includes('${')) {
            return basePortrait || 'generic';
        }
        // Always use the player's userId for user-specific operations
        const playerUserId = player.userId || 'default';
        const context = {
            playerId: player.id,
            characterName: player.characterName,
            currentSceneId: player.currentScene,
            userId: playerUserId
        };
        const resolved = this.interpolationPipeline.interpolateString(basePortrait, context);
        const trimmed = (resolved ?? '').trim();
        return trimmed.length > 0 ? trimmed : 'generic';
    }
    /**
     * Initialize scene variables for ALL scenes in the adventure
     * Scene variables are shared across all players in a session
     */
    async initializeSceneVariables() {
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        if (!adventure || !adventure.scenes) {
            return;
        }
        // Process scene variables from all scenes in the adventure
        for (const scene of adventure.scenes) {
            if (scene.variables) {
                const sceneVars = new Map();
                for (const variable of scene.variables) {
                    sceneVars.set(variable.name, {
                        name: variable.name,
                        value: variable.defaultValue,
                        type: variable.type
                    });
                }
                // Initialize scene variables in VariableStore for this scene
                if (this.variableStore) {
                    this.variableStore.initializeSceneVariables(scene.id, sceneVars);
                }
            }
        }
    }
    /**
     * Initialize variables for all existing players
     */
    async initializeAllPlayerVariables() {
        // First, initialize scene variables for ALL scenes (shared across session)
        await this.initializeSceneVariables();
        // Hydrate every scene's persisted variables from the DB so off-scene data (e.g. chat history)
        // is not left at adventure defaults when the engine is created while the player is elsewhere.
        await this.loadAllPersistedSceneVariablesFromBackend();
        // Load persisted character-scoped variables for NPCs (e.g. character_location) before player
        // defaults run, so robots stay active across backend restarts the same way player data does.
        await this.loadNpcCharacterVariablesFromBackend();
        // Then initialize player-specific variables
        const players = this.gameSession.players;
        for (const player of players) {
            await this.initializePlayerVariables(player);
        }
    }
    /**
     * Load persisted scene variables for every scene in the adventure that declares variables.
     */
    async loadAllPersistedSceneVariablesFromBackend() {
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        if (!adventure?.scenes) {
            return;
        }
        const anchor = this.gameSession.players[0];
        if (!anchor) {
            return;
        }
        const context = {
            playerId: anchor.id,
            userId: anchor.userId || 'anonymous',
        };
        for (const scene of adventure.scenes) {
            if (!scene.variables || scene.variables.length === 0) {
                continue;
            }
            try {
                await this.variableStore.loadSceneVariablesFromBackend(scene.id, context);
            }
            catch (error) {
                console.error(`[GameEngine] Failed to load persisted scene variables for '${scene.id}':`, error);
            }
        }
    }
    /**
     * Hydrate VariableStore from the backend for each adventure NPC definition.
     * Uses the same anchor context as {@link NpcManager} (first session player).
     */
    async loadNpcCharacterVariablesFromBackend() {
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        if (!adventure) {
            return;
        }
        const npcs = adventure.getNpcDefinitions();
        if (npcs.length === 0) {
            return;
        }
        const anchor = this.gameSession.players[0];
        if (!anchor) {
            return;
        }
        const playerUserId = anchor.userId || 'anonymous';
        for (const npc of npcs) {
            const context = {
                playerId: anchor.id,
                characterName: npc.name,
                currentSceneId: anchor.currentScene,
                userId: playerUserId
            };
            try {
                await this.variableStore.loadExistingVariables(context);
            }
            catch (error) {
                console.error(`[GameEngine] Failed to load persisted variables for NPC '${npc.name}':`, error);
            }
        }
    }
    /**
     * Initialize variables for a player when they join
     */
    async initializePlayerVariables(player) {
        // Always use the player's userId for user-specific operations
        const playerUserId = player.userId || 'default';
        const context = {
            playerId: player.id,
            characterName: player.characterName,
            currentSceneId: player.currentScene,
            userId: playerUserId
        };
        // Load existing variables from backend first
        try {
            await this.variableStore.loadExistingVariables(context);
        }
        catch (error) {
            console.error('Failed to load existing variables from backend:', error);
        }
        // Get adventure variable definitions
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        if (!adventure) {
            throw new Error(`Adventure '${this.gameSession.adventureId}' not found`);
        }
        const adventureHeader = adventure.getHeader();
        // Build variable maps from adventure definition (excluding scene variables)
        const adventureVariables = new Map();
        const characterVariables = new Map();
        // Process adventure variables (from adventure data, not header)
        if (adventure.variables) {
            for (const variable of adventure.variables) {
                adventureVariables.set(variable.name, {
                    name: variable.name,
                    value: variable.defaultValue,
                    type: variable.type
                });
            }
        }
        // Process character variables: first apply adventure-level character variables to all characters,
        // then overlay per-character variables (character-specific overrides)
        if (adventureHeader.characters) {
            for (const character of adventureHeader.characters) {
                const charVars = new Map();
                // 1. Apply variables from ## Character Variables (applies to all characters)
                if (adventure.characterVariables) {
                    for (const variable of adventure.characterVariables) {
                        charVars.set(variable.name, {
                            name: variable.name,
                            value: variable.defaultValue,
                            type: variable.type
                        });
                    }
                }
                // 2. Overlay per-character variables (character-specific overrides)
                if (character.variables) {
                    for (const variable of character.variables) {
                        charVars.set(variable.name, {
                            name: variable.name,
                            value: variable.defaultValue,
                            type: variable.type
                        });
                    }
                }
                characterVariables.set(character.name, charVars);
            }
        }
        // Open-character instances store vars under the instance name; seed from template defaults.
        if ((0, characterInstanceNames_1.isCharacterInstanceName)(context.characterName)) {
            const templateName = (0, characterInstanceNames_1.getCharacterTemplateName)(context.characterName);
            const templateVars = characterVariables.get(templateName);
            if (templateVars) {
                characterVariables.set(context.characterName, new Map(templateVars));
            }
        }
        // Initialize default values (only if not already loaded from backend)
        // Note: scene variables are initialized separately in initializeSceneVariables()
        this.variableStore.initializeDefaults(this.variablesConfig, adventureVariables, characterVariables, new Map(), // Empty sceneVariables map - they're handled in initializeSceneVariables()
        context);
    }
    /**
     * Get variable value for a player
     */
    getVariable(playerId, variableName) {
        const player = this.getPlayer(playerId);
        if (!player)
            return undefined;
        // Always use the player's userId for user-specific operations
        const playerUserId = player.userId || 'default';
        const context = {
            playerId: player.id,
            characterName: player.characterName,
            currentSceneId: player.currentScene,
            userId: playerUserId
        };
        return this.variableStore.getVariable(variableName, context);
    }
    /**
     * Set variable value for a player
     * This ensures variables are set through the same path as scripts (maintains cache sync)
     * Used by backend services to update variables
     * @param playerId Player ID
     * @param variableName Variable name
     * @param value Variable value
     * @param context Optional variable context (will be built from player if not provided)
     */
    setVariable(playerId, variableName, value, context, options) {
        const player = this.getPlayer(playerId);
        if (!player) {
            return false;
        }
        // Build context if not provided
        // Always use player.userId for user-specific variable operations
        const variableContext = context || VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
        try {
            // Infer variable type (same as scripts)
            const variableType = VariableStore_1.VariableStore.inferVariableType(value);
            this.variableStore.setVariable(variableName, value, variableContext, variableType, options);
            return true;
        }
        catch (error) {
            console.error('Failed to set variable:', error);
            return false;
        }
    }
    /**
     * Select a sync option: add the clicking character to syncVar, and when all required
     * characters have clicked, move them to targetScene (or per-character targets).
     */
    selectSyncOption(playerId, syncVar, targetScene, characterNames, targetByCharacter, syncEffects) {
        const player = this.getPlayer(playerId);
        if (!player) {
            throw new Error(`Player '${playerId}' not found`);
        }
        const adventure = this.adventureManager.getAdventure(this.gameSession.adventureId);
        if (!adventure) {
            throw new Error(`Adventure '${this.gameSession.adventureId}' not loaded`);
        }
        const characterName = player.characterName;
        const requiredCharacters = characterNames && characterNames.length > 0 ? characterNames : [characterName];
        const useTargetByCharacter = targetByCharacter &&
            typeof targetByCharacter === 'object' &&
            requiredCharacters.every((c) => typeof targetByCharacter[c] === 'string' && targetByCharacter[c].trim() !== '');
        if (useTargetByCharacter) {
            for (const c of requiredCharacters) {
                const sceneId = targetByCharacter[c].trim();
                if (!adventure.getScene(sceneId)) {
                    throw new Error(`Scene '${sceneId}' (for character '${c}') not found in adventure`);
                }
            }
        }
        else if (!targetScene || typeof targetScene !== 'string' || targetScene.trim() === '') {
            throw new Error('targetScene is required when targetByCharacter is not provided');
        }
        else if (!adventure.getScene(targetScene)) {
            throw new Error(`Scene '${targetScene}' not found in adventure`);
        }
        let currentValue = this.getVariable(playerId, syncVar);
        if (currentValue === undefined || currentValue === null) {
            currentValue = '[]';
        }
        const raw = String(currentValue).trim();
        let clicked = [];
        try {
            if (raw) {
                const parsed = JSON.parse(raw);
                clicked = Array.isArray(parsed)
                    ? parsed.filter((x) => typeof x === 'string')
                    : [];
            }
        }
        catch {
            clicked = [];
        }
        if (!clicked.includes(characterName)) {
            clicked = [...clicked, characterName];
        }
        this.setVariable(playerId, syncVar, JSON.stringify(clicked), undefined, {
            allowUndeclaredAdventure: true,
        });
        const allClicked = requiredCharacters.every((c) => clicked.includes(c));
        if (!allClicked) {
            return { allClicked: false };
        }
        this.setVariable(playerId, syncVar, '[]', undefined, { allowUndeclaredAdventure: true });
        if (typeof syncEffects === 'string' && syncEffects.trim() !== '') {
            const effectPlayerId = requiredCharacters
                .map((c) => this.gameSession.players.find((p) => this.getPlayer(p.id)?.characterName === c)?.id)
                .find(Boolean) ?? playerId;
            const effectPlayer = this.getPlayer(effectPlayerId);
            if (effectPlayer) {
                this.runEffectScript(syncEffects.trim(), effectPlayer);
            }
        }
        for (const sessionPlayer of this.gameSession.players) {
            const enginePlayer = this.getPlayer(sessionPlayer.id);
            if (!enginePlayer || !requiredCharacters.includes(enginePlayer.characterName)) {
                continue;
            }
            const sceneForPlayer = useTargetByCharacter
                ? (targetByCharacter[enginePlayer.characterName] ?? targetScene).trim()
                : targetScene;
            const targetSceneData = adventure.getScene(sceneForPlayer);
            if (!targetSceneData) {
                continue;
            }
            this.pushStack(enginePlayer.id, 'return_scene', enginePlayer.currentScene);
            enginePlayer.moveToScene(sceneForPlayer);
            if (targetSceneData.locationTag) {
                this.setVariable(enginePlayer.id, 'character_location', targetSceneData.locationTag);
            }
            this.setVariable(enginePlayer.id, 'user_scene_enter_timestamp', Math.floor(Date.now() / 1000));
            if (targetSceneData.autoEffect) {
                this.runEffectScript(targetSceneData.autoEffect, enginePlayer);
            }
            this.gameSession.updatePlayer(enginePlayer);
        }
        return { allClicked: true };
    }
    /**
     * Handle a user input update for a declared variable
     */
    handleUserInput(playerId, variableName, rawValue, options = {}) {
        const baseResult = {
            success: false,
            playerId,
            variableName,
            errors: []
        };
        const player = this.getPlayer(playerId);
        if (!player) {
            return {
                ...baseResult,
                errors: [`Player '${playerId}' not found`]
            };
        }
        const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'default');
        const metadata = this.variableStore.getVariableMetadata(variableName, context);
        if (!metadata) {
            return {
                ...baseResult,
                errors: [`Variable '${variableName}' is not declared in the current context`]
            };
        }
        if (metadata.type !== 'string') {
            return {
                ...baseResult,
                scope: metadata.scope,
                errors: [`Variable '${variableName}' is not a string and cannot be updated through user input`]
            };
        }
        const requestedMaxLength = options.maxLength !== undefined && Number.isFinite(options.maxLength)
            ? Math.floor(options.maxLength)
            : undefined;
        let effectiveMaxLength = requestedMaxLength && requestedMaxLength > 0
            ? requestedMaxLength
            : DEFAULT_USER_INPUT_MAX_LENGTH;
        if (effectiveMaxLength > ABSOLUTE_USER_INPUT_MAX_LENGTH) {
            effectiveMaxLength = ABSOLUTE_USER_INPUT_MAX_LENGTH;
        }
        const value = (rawValue !== undefined && rawValue !== null ? String(rawValue) : '').trim();
        if (value.length > effectiveMaxLength) {
            return {
                ...baseResult,
                scope: metadata.scope,
                maxLengthApplied: effectiveMaxLength,
                errors: [`Value exceeds maximum length of ${effectiveMaxLength} characters`]
            };
        }
        if (!this.interpolationPipeline.validateVariableValue(value)) {
            return {
                ...baseResult,
                scope: metadata.scope,
                maxLengthApplied: effectiveMaxLength,
                errors: ['Value cannot contain variable placeholders (e.g. ${variable_name})']
            };
        }
        const previousValue = typeof metadata.value === 'string' ? metadata.value : undefined;
        try {
            this.variableStore.setVariable(variableName, value, context, 'string');
        }
        catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error while updating variable';
            return {
                ...baseResult,
                scope: metadata.scope,
                maxLengthApplied: effectiveMaxLength,
                errors: [message]
            };
        }
        const broadcastPayload = {
            sessionId: this.gameSession.id,
            playerId: player.id,
            variableName,
            value,
            previousValue,
            scope: metadata.scope
        };
        return {
            success: true,
            playerId: player.id,
            variableName,
            value,
            previousValue,
            scope: metadata.scope,
            maxLengthApplied: effectiveMaxLength,
            broadcastPayload
        };
    }
    /**
     * Push a value onto a stack variable
     */
    pushStack(playerId, variableName, value) {
        const player = this.getPlayer(playerId);
        if (!player) {
            return false;
        }
        const context = {
            playerId: player.id,
            characterName: player.characterName,
            currentSceneId: player.currentScene,
            userId: player.userId || 'default'
        };
        try {
            this.variableStore.pushStack(variableName, value, context);
            return true;
        }
        catch (error) {
            console.error('Failed to push to stack:', error);
            return false;
        }
    }
    /**
     * Pop a value from a stack variable
     */
    popStack(playerId, variableName) {
        const player = this.getPlayer(playerId);
        if (!player) {
            return undefined;
        }
        const context = {
            playerId: player.id,
            characterName: player.characterName,
            currentSceneId: player.currentScene,
            userId: player.userId || 'default'
        };
        try {
            return this.variableStore.popStack(variableName, context);
        }
        catch (error) {
            console.error('Failed to pop from stack:', error);
            return undefined;
        }
    }
    /**
     * Peek at the top of a stack variable without popping
     */
    peekStack(playerId, variableName) {
        const player = this.getPlayer(playerId);
        if (!player) {
            return undefined;
        }
        const context = {
            playerId: player.id,
            characterName: player.characterName,
            currentSceneId: player.currentScene,
            userId: player.userId || 'default'
        };
        return this.variableStore.peekStack(variableName, context);
    }
    /**
     * Get the depth (length) of a stack variable
     */
    getStackDepth(playerId, variableName) {
        const player = this.getPlayer(playerId);
        if (!player) {
            return 0;
        }
        const context = {
            playerId: player.id,
            characterName: player.characterName,
            currentSceneId: player.currentScene,
            userId: player.userId || 'default'
        };
        const stack = this.variableStore.getStackVariable(variableName, context);
        return stack ? stack.length : 0;
    }
    /**
     * Get the entire stack array for a stack variable
     */
    getStackArray(playerId, variableName) {
        const player = this.getPlayer(playerId);
        if (!player) {
            return [];
        }
        const context = {
            playerId: player.id,
            characterName: player.characterName,
            currentSceneId: player.currentScene,
            userId: player.userId || 'default'
        };
        const stack = this.variableStore.getStackVariable(variableName, context);
        return stack || [];
    }
    /**
     * Pop values from a stack until it reaches a specific depth
     * Returns the number of values popped
     */
    popStackToDepth(playerId, variableName, targetDepth) {
        const player = this.getPlayer(playerId);
        if (!player) {
            return 0;
        }
        const context = {
            playerId: player.id,
            characterName: player.characterName,
            currentSceneId: player.currentScene,
            userId: player.userId || 'default'
        };
        const currentStack = this.variableStore.getStackVariable(variableName, context);
        if (!currentStack) {
            return 0;
        }
        const currentDepth = currentStack.length;
        if (currentDepth <= targetDepth) {
            return 0;
        }
        const popsNeeded = currentDepth - targetDepth;
        for (let i = 0; i < popsNeeded; i++) {
            try {
                this.variableStore.popStack(variableName, context);
            }
            catch (error) {
                console.warn(`Failed to pop from stack '${variableName}' at iteration ${i}:`, error);
                break;
            }
        }
        return popsNeeded;
    }
    /**
     * Get the stored return stack depth for a menu scene
     * Returns undefined if not set
     */
    getMenuReturnStackDepth(playerId, menuScene) {
        const variableName = menuScene === 'character_menu'
            ? 'character_menu_return_stack_depth'
            : 'adventure_menu_return_stack_depth';
        const value = this.getVariable(playerId, variableName);
        if (value === undefined || value === null) {
            return undefined;
        }
        // Convert to number if it's a string
        if (typeof value === 'string') {
            const num = parseInt(value, 10);
            return isNaN(num) ? undefined : num;
        }
        return typeof value === 'number' ? value : undefined;
    }
    /**
     * Set the stored return stack depth for a menu scene
     */
    setMenuReturnStackDepth(playerId, menuScene, depth) {
        const variableName = menuScene === 'character_menu'
            ? 'character_menu_return_stack_depth'
            : 'adventure_menu_return_stack_depth';
        return this.setVariable(playerId, variableName, depth);
    }
    /**
     * Clear the stored return stack depth for a menu scene
     */
    clearMenuReturnStackDepth(playerId, menuScene) {
        const variableName = menuScene === 'character_menu'
            ? 'character_menu_return_stack_depth'
            : 'adventure_menu_return_stack_depth';
        const player = this.getPlayer(playerId);
        if (!player) {
            return false;
        }
        // Setting to -1 as a sentinel value to indicate "not set"
        return this.setVariable(playerId, variableName, -1);
    }
    /**
     * Check if a menu return stack depth is set (not cleared)
     */
    hasMenuReturnStackDepth(playerId, menuScene) {
        const depth = this.getMenuReturnStackDepth(playerId, menuScene);
        return depth !== undefined && depth !== null && depth >= 0;
    }
    isReturnSceneTarget(rawTarget) {
        return rawTarget.includes('${return_scene}');
    }
    captureMenuReturnContext(player, rawTarget) {
        if (!this.isReturnSceneTarget(rawTarget)) {
            return undefined;
        }
        const menuScene = player.currentScene === 'character_menu' || player.currentScene === 'adventure_menu'
            ? player.currentScene
            : undefined;
        if (!menuScene) {
            return undefined;
        }
        const storedDepth = this.getMenuReturnStackDepth(player.id, menuScene);
        if (storedDepth === undefined || storedDepth < 0) {
            return undefined;
        }
        return { menuScene, storedDepth };
    }
    resolveReturnSceneTarget(player, rawTarget) {
        if (this.pendingMenuReturnContext) {
            const { storedDepth } = this.pendingMenuReturnContext;
            const currentStack = this.getStackArray(player.id, 'return_scene');
            let returnScene;
            if (currentStack.length > storedDepth) {
                returnScene = currentStack[storedDepth];
            }
            else if (currentStack.length > 0) {
                returnScene = currentStack[0];
            }
            if (returnScene) {
                return returnScene;
            }
        }
        return this.interpolateTarget(player, rawTarget);
    }
    applyReturnSceneStackPop(playerId, interpolatedTarget) {
        if (this.pendingMenuReturnContext) {
            const { menuScene, storedDepth } = this.pendingMenuReturnContext;
            this.pendingMenuReturnContext = undefined;
            const isReturnSceneAMenu = interpolatedTarget === 'character_menu' || interpolatedTarget === 'adventure_menu';
            this.popStackToDepth(playerId, 'return_scene', storedDepth);
            this.clearMenuReturnStackDepth(playerId, menuScene);
            if (!isReturnSceneAMenu) {
                const stackAfterPop = this.getStackArray(playerId, 'return_scene');
                if (stackAfterPop.length > 0 &&
                    stackAfterPop[stackAfterPop.length - 1] === interpolatedTarget) {
                    this.popStack(playerId, 'return_scene');
                }
            }
            return;
        }
        try {
            const topScene = this.peekStack(playerId, 'return_scene');
            if (topScene && topScene === interpolatedTarget) {
                this.popStack(playerId, 'return_scene');
            }
            else if (topScene) {
                this.popStack(playerId, 'return_scene');
                console.warn(`Return scene mismatch: stack had ${topScene}, navigating to ${interpolatedTarget}`);
            }
        }
        catch (error) {
            console.warn(`Could not pop return_scene stack: ${error}`);
        }
    }
    /**
     * Interpolate variables in option target strings
     */
    interpolateTarget(player, target) {
        // Build context for interpolation
        const context = {
            playerId: player.id,
            characterName: player.characterName,
            currentSceneId: player.currentScene,
            userId: player.userId || 'default'
        };
        // Interpolate the target string
        return this.interpolationPipeline.interpolateString(target, context);
    }
    /**
     * Check if a target string is a script target (contains ''' delimiters)
     */
    isScriptTarget(target) {
        if (!target || typeof target !== 'string') {
            return false;
        }
        // Check if target contains triple single quotes (script delimiter)
        return target.includes("'''");
    }
    /**
     * Evaluate a script target and return the target string
     */
    evaluateTargetScript(script, player, gameSession) {
        try {
            // Extract script content from '''script''' format (remove delimiters)
            // Script targets are stored with delimiters, so we need to remove them
            let scriptContent = script.trim();
            if (scriptContent.startsWith("'''") && scriptContent.endsWith("'''")) {
                scriptContent = scriptContent.slice(3, -3).trim();
            }
            // Evaluate script using ScriptEvaluator
            const result = ScriptEvaluator_1.ScriptEvaluator.executeScriptWithReturnValue(scriptContent, player, gameSession, this.variableStore, this.adventureManager, this.friendsService, this.adventureInviteService, this.asyncOperationService, this.inviteLinkService, this.emailService, this.emailActionTokenService, this.verificationCodeService, this.analyticsService);
            // Convert result to string (handle null/undefined by returning empty string)
            if (result === null || result === undefined) {
                console.warn(`Script target evaluation returned null/undefined: ${script}`);
                return '';
            }
            return String(result);
        }
        catch (error) {
            console.error(`❌ Script target evaluation failed: "${script}"`, error);
            if (error instanceof Error) {
                console.error(`❌ Error message:`, error.message);
                console.error(`❌ Error stack:`, error.stack);
            }
            // Re-throw to let caller handle the error
            throw error;
        }
    }
    /** Export session + variable state for runtime snapshots (excludes adventure definitions). */
    exportRuntimeState(options) {
        return {
            schemaVersion: RuntimeSnapshot_1.RUNTIME_SNAPSHOT_SCHEMA_VERSION,
            adventureId: this.gameSession.adventureId,
            capturedAt: new Date().toISOString(),
            clockMs: options?.clockMs,
            session: (0, RuntimeSnapshot_1.sessionDataFromGameSession)(this.gameSession),
            variables: this.variableStore.exportAllScopes(),
        };
    }
    /** Restore variable scopes from a snapshot (session must already match). */
    restoreVariableState(variables) {
        this.variableStore.importAllScopes(variables);
    }
    /**
     * Validate and apply a full runtime snapshot to this engine.
     * Replaces the in-memory session and variable store; does not run {@link startGame}.
     */
    importRuntimeState(snapshot) {
        const validated = (0, RuntimeSnapshot_1.assertRuntimeSnapshot)(snapshot, this.gameSession.adventureId);
        this.gameSession.players = validated.session.players.map((p) => new Player_1.Player(p));
        this.gameSession.locationInventories = new Map(Object.entries(validated.session.locationInventories ?? {}));
        this.gameSession.setStatus(validated.session.status);
        this.variableStore.importAllScopes(validated.variables);
    }
}
exports.GameEngine = GameEngine;
//# sourceMappingURL=GameEngine.js.map